import axios from 'axios';

const getApiBaseUrl = () => {
  const configuredApiUrl = process.env.REACT_APP_API_URL?.trim();

  if (configuredApiUrl) {
    return configuredApiUrl;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'http://localhost:5000';
};

// xAI Added: Structured logging to file
const logToFile = (level, message, meta = {}) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    ...meta,
  };
  // In production, write to a file or send to a logging service
  console[level](JSON.stringify(logEntry));
};

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: 30000, // Increased for slow MT5 connections
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  validateStatus: (status) => status < 500,
});

// Retry logic with exponential backoff
const retryRequest = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === maxRetries - 1 || err.response?.status === 401) {
        logToFile('error', 'Retry limit reached or unauthorized', {
          attempt: i + 1,
          maxRetries,
          error: err.message,
          status: err.response?.status,
        });
        throw err;
      }
      const delay = baseDelay * 2 ** i + Math.random() * 100; // Exponential backoff
      logToFile('warn', `Retrying request (${i + 1}/${maxRetries}) after ${delay}ms`, {
        url: err.config?.url,
      });
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

// xAI Added: Basic JWT validation
const isValidJwt = (token) => {
  if (typeof token !== 'string' || token.trim() === '') return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    // Decode payload to check structure
    const payload = JSON.parse(atob(parts[1]));
    return payload && typeof payload === 'object' && (payload.id || payload.sub);
  } catch (e) {
    logToFile('error', 'Invalid JWT format', { error: e.message });
    return false;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const publicEndpoints = ['/api/login', '/api/register', '/api/forgot-password', '/api/reset-password'];
    if (publicEndpoints.some((endpoint) => config.url?.includes(endpoint)) || config.__isRetryRequest) {
      return config;
    }

    const token = localStorage.getItem('token');
    if (token) {
      // xAI Modified: Strengthen token validation
      if (!isValidJwt(token)) {
        logToFile('error', 'Invalid token detected, removing from storage', { tokenLength: token.length });
        localStorage.removeItem('token');
      } else {
        config.headers['Authorization'] = `Bearer ${token}`;
        logToFile('debug', 'Added Authorization header', { url: config.url });
      }
    }

    return config;
  },
  (error) => {
    logToFile('error', 'Request interceptor error', { error: error.message });
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (error.code === 'ECONNABORTED' || !error.response) {
      const message = error.code === 'ECONNABORTED' ? 'Request timed out. Please try again.' : 'Unable to connect to the server. Please check your internet connection.';
      logToFile('error', 'Network error', {
        message,
        url: originalRequest?.url,
        error: error.message,
      });
      return Promise.reject({ message, details: error.message, errorType: 'network' });
    }

    // Handle 401 Unauthorized
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      logToFile('warn', 'Unauthorized, attempting token refresh', { url: originalRequest.url });
      try {
        // xAI Modified: Add retry logic for refresh token
        const refreshFn = () =>
          axios.post(
            `${getApiBaseUrl()}/api/refresh-token`,
            {},
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                // xAI Added: Avoid X-Requested-With for refresh to minimize CORS risks
              },
            }
          );
        const response = await retryRequest(refreshFn, 2, 500);

        if (response.data.access_token) {
          const { access_token } = response.data;
          localStorage.setItem('token', access_token);
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          logToFile('info', 'Token refreshed successfully', { url: originalRequest.url });
          return api(originalRequest);
        }
      } catch (refreshError) {
        logToFile('error', 'Token refresh failed', {
          url: originalRequest.url,
          error: refreshError.response?.data || refreshError.message,
        });
        localStorage.removeItem('token');
        document.cookie = 'access_token=; Max-Age=0; path=/;';
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userId');
        localStorage.removeItem('isLoggedIn');

        // xAI Modified: Avoid redirect if already on login page
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
        return Promise.reject({
          message: 'Session expired. Please log in again.',
          details: refreshError.response?.data?.details || refreshError.message,
          errorType: 'auth',
        });
      }
    }

    // Retry non-401 errors
    if (!originalRequest._retry && error.response.status !== 401) {
      originalRequest._retry = true;
      try {
        return await retryRequest(() => api(originalRequest));
      } catch (retryError) {
        error = retryError; // Update error to the latest retry failure
      }
    }

    // Handle other errors with detailed propagation
    const errorData = error.response?.data || {};
    const validationDetail = Array.isArray(errorData.detail)
      ? errorData.detail.map((item) => item?.msg || item).join(' ')
      : typeof errorData.detail === 'string'
        ? errorData.detail
        : errorData.detail?.message;
    const message = errorData.message || validationDetail || errorData.details || 'An unexpected error occurred';
    const details = errorData.details || validationDetail || errorData.error_type || error.message;
    const errorType = errorData.error_type || 'unknown';
    logToFile('error', `API error (${error.response?.status})`, {
      message,
      details,
      errorType,
      url: originalRequest?.url,
      response: errorData,
    });

    if (error.response?.status === 403) {
      return Promise.reject({ message: 'Forbidden: You do not have permission to access this resource', details, errorType, status: error.response?.status, response: error.response });
    } else if (error.response?.status === 404) {
      return Promise.reject({ message: 'Not Found: The requested resource was not found', details, errorType, status: error.response?.status, response: error.response });
    }

    return Promise.reject({ message, details, errorType, status: error.response?.status, response: error.response });
  }
);

export default api;